import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SIZE } from '../tokens/space.js';
/**
 * The page frame (§5). Two widths and no others: `full` fills the content area (1400px,
 * set by the shell); `narrow` is for forms, settings and other single-column reading pages.
 */
export function Page({ children, width = 'full', }) {
    return (_jsx(Box, { sx: {
            width: '100%',
            maxWidth: width === 'narrow' ? SIZE.narrowMaxWidth : 'none',
            mx: width === 'narrow' ? 'auto' : 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            minWidth: 0,
            flexGrow: 1,
        }, children: children }));
}
/**
 * The only h1 on a page: a 22px title, an optional one-line meta beneath it, actions on
 * the right (wrapping under on phones) and an optional breadcrumb row above.
 */
export function PageHeader({ title, meta, actions, breadcrumb, id, }) {
    return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [breadcrumb && (_jsx(Typography, { variant: "caption", color: "text.tertiary", component: "div", sx: { display: 'flex', alignItems: 'center', gap: 0.75 }, children: breadcrumb })), _jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, sx: {
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    justifyContent: 'space-between',
                    gap: { xs: 1.5, sm: 2 },
                }, children: [_jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { variant: "h2", component: "h1", ...(id ? { id } : {}), sx: { textWrap: 'balance' }, children: title }), meta && (_jsx(Typography, { variant: "body2", color: "text.secondary", component: "div", sx: { mt: 0.5 }, children: meta }))] }), actions && (_jsx(Stack, { direction: "row", spacing: 1, sx: { alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', gap: 1, rowGap: 1 }, children: actions }))] })] }));
}
/** A section title inside the page or a card: 14px/600 with an optional right-hand slot. */
export function SectionHeader({ title, meta, action, component = 'h2', }) {
    return (_jsxs(Stack, { direction: "row", sx: { alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.25 }, children: [_jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { variant: "h6", component: component, children: title }), meta && (_jsx(Typography, { variant: "caption", color: "text.tertiary", component: "div", children: meta }))] }), action && _jsx(Box, { sx: { flexShrink: 0 }, children: action })] }));
}
/**
 * A key–value list for a record's profile column (§1.2: "a key–value list, not six stacked
 * cards"). Renders a description list so the pairs are announced as pairs.
 */
export function KeyValueList({ items }) {
    return (_jsx(Box, { component: "dl", sx: {
            m: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 160px) 1fr' },
            columnGap: 3,
            rowGap: { xs: 0.5, sm: 1.25 },
        }, children: items.map((item) => (_jsxs(Box, { sx: { display: 'contents' }, children: [_jsx(Typography, { component: "dt", variant: "body2", color: "text.secondary", sx: { py: { sm: 0.25 } }, children: item.key }), _jsx(Typography, { component: "dd", variant: "body2", sx: { m: 0, mb: { xs: 1, sm: 0 }, py: { sm: 0.25 }, minWidth: 0 }, children: item.value })] }, item.key))) }));
}
//# sourceMappingURL=page.js.map